import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, Volume2, VolumeX, Mic, Square } from "lucide-react";
import { postLead } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const WhatsAppAssistant = ({ whatsapp }) => {
  const { t, lang } = useLanguage();
  const TOPICS = t.assistant.topics;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [topic, setTopic] = useState(null);
  const [name, setName] = useState("");
  const [luogo, setLuogo] = useState("");
  const [azienda, setAzienda] = useState("");
  const [settore, setSettore] = useState(null);
  const [contatto, setContatto] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("xs:open-whatsapp", handler);
    return () => window.removeEventListener("xs:open-whatsapp", handler);
  }, []);

  const number = (whatsapp || "41782042172").replace(/\D/g, "");

  const topicLabel = TOPICS.find((tp) => tp.id === topic)?.label || topic;
  const settoreLabel = t.assistant.settoreOptions.find((s) => s.id === settore)?.label || settore || "";
  const buildMessage = () =>
    t.assistant.waMessage
      .replace("{name}", name)
      .replace("{topic}", topicLabel)
      .replace("{luogo}", luogo)
      .replace("{azienda}", azienda)
      .replace("{settore}", settoreLabel)
      .replace("{contatto}", contatto)
      .replace("{message}", message);

  const openWhatsApp = async () => {
    try {
      await postLead({ name, topic: topicLabel, luogo, azienda, settore: settoreLabel, contatto, message, source: "whatsapp-widget" });
    } catch {
      // il lead non blocca l'apertura di WhatsApp
    }
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(buildMessage())}`, "_blank");
  };

  const reset = () => {
    setStep(0);
    setTopic(null);
    setName("");
    setLuogo("");
    setAzienda("");
    setSettore(null);
    setContatto("");
    setMessage("");
  };

  // ——— Guida vocale (TTS + STT) ———
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("xs-voice") !== "0");
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const audioRef = useRef(null);
  const mediaRef = useRef(null);
  const ttsCache = useRef(new Map());
  const API = process.env.REACT_APP_BACKEND_URL;
  const setters = { 1: setName, 2: setLuogo, 3: setAzienda, 5: setContatto, 6: setMessage };

  const stepText = (s) => {
    if (s === 0) return t.assistant.steps[0];
    if (s === 1) return t.assistant.questions.name;
    if (s === 2) return t.assistant.questions.luogo;
    if (s === 3) return t.assistant.questions.azienda;
    if (s === 4) return t.assistant.questions.settore;
    if (s === 5) return t.assistant.questions.contatto;
    if (s === 6) return t.assistant.questions.message.replace("{name}", name);
    if (s === 7) return t.assistant.steps[3];
    return "";
  };

  const speak = async (text, force = false) => {
    if ((!voiceOn && !force) || !text) return;
    try {
      setSpeaking(true);
      let blob = ttsCache.current.get(text);
      if (!blob) {
        const res = await fetch(`${API}/api/voice/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error("tts");
        blob = await res.blob();
        ttsCache.current.set(text, blob);
      }
      audioRef.current?.pause();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };

  useEffect(() => {
    if (open && voiceOn) speak(stepText(step));
    if (!open) audioRef.current?.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open, lang]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("xs-voice", next ? "1" : "0");
    if (!next) audioRef.current?.pause();
    if (next) speak(stepText(step), true);
  };

  const startRecording = async (setter) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "audio.webm");
        fd.append("lang", lang);
        try {
          const res = await fetch(`${API}/api/voice/stt`, { method: "POST", body: fd });
          const data = await res.json();
          if (data.text) setter(data.text);
        } catch {
          // trascrizione fallita: l'utente può riscrivere
        }
        setRecording(false);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      // microfono non autorizzato
    }
  };

  const stopRecording = () => mediaRef.current?.stop();

  // ——— Chat libera con IA ———
  const sessionRef = useRef(
    localStorage.getItem("xs-chat-session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("xs-chat-session", id);
        return id;
      })()
  );
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const sendChatText = async (text) => {
    const clean = text.trim();
    if (!clean || chatLoading) return;
    setChatMode(true);
    setChatMessages((m) => [...m, { role: "user", text: clean }, { role: "assistant", text: "" }]);
    setChatInput("");
    setChatLoading(true);
    let acc = "";
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: clean, lang }),
      });
      if (!res.ok || !res.body) throw new Error("chat");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ") && line !== "data: [DONE]") {
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.error) throw new Error("chat");
              acc += payload.t;
            } catch (parseErr) {
              if (parseErr.message === "chat") throw parseErr;
            }
          }
        }
        const snapshot = acc;
        setChatMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", text: snapshot };
          return c;
        });
      }
      if (voiceOn && acc) speak(acc, true);
    } catch {
      setChatMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: t.assistant.chatError };
        return c;
      });
    }
    setChatLoading(false);
  };


  return (
    <div className="fixed bottom-6 right-6 z-[65] flex flex-col items-end gap-4">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-panel"
            className="glass-panel rounded-2xl w-[calc(100vw-32px)] max-w-[360px] sm:w-[360px] overflow-hidden"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-neon-cyan/10 to-neon-violet/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-neon-cyan to-neon-violet flex items-center justify-center">
                  <Bot size={18} className="text-void" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.assistant.name}</p>
                  <p className="text-xs text-neon-cyan flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan inline-block animate-pulse" /> {t.assistant.status}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleVoice}
                  data-testid="voice-toggle-button"
                  aria-label="Attiva o disattiva guida vocale"
                  className={`transition-colors ${voiceOn ? "text-neon-cyan" : "text-white/50 hover:text-white"}`}
                >
                  {voiceOn ? <Volume2 size={18} className={speaking ? "animate-pulse" : ""} /> : <VolumeX size={18} />}
                </button>
                <button onClick={() => setOpen(false)} data-testid="whatsapp-close-button" className="text-white/50 hover:text-white" aria-label="Chiudi assistente">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5 flex flex-col gap-4 min-h-[220px]">
              {!chatMode && (
              <>
              <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-white/80" data-testid="whatsapp-step-message">
                {step === 0 && t.assistant.steps[0]}
                {step === 1 && t.assistant.questions.name}
                {step === 2 && t.assistant.questions.luogo}
                {step === 3 && t.assistant.questions.azienda}
                {step === 4 && t.assistant.questions.settore}
                {step === 5 && t.assistant.questions.contatto}
                {step === 6 && t.assistant.questions.message.replace("{name}", name)}
                {step === 7 && t.assistant.steps[3]}
              </div>

              {step === 0 && (
                <div className="flex flex-wrap gap-2">
                  {TOPICS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { setTopic(t.id); setStep(1); }}
                      data-testid={`whatsapp-topic-${t.id}`}
                      className="rounded-full border border-neon-cyan/40 px-3 py-2 text-xs text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {step >= 1 && step <= 6 && step !== 4 && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const vals = { 1: name, 2: luogo, 3: azienda, 5: contatto, 6: message };
                    if (step !== 6 && !vals[step].trim()) return;
                    setStep(step + 1);
                  }}
                  className="flex gap-2"
                >
                  <input
                    autoFocus
                    value={{ 1: name, 2: luogo, 3: azienda, 5: contatto, 6: message }[step]}
                    onChange={(e) => setters[step](e.target.value)}
                    placeholder={{ 1: t.assistant.namePlaceholder, 2: t.assistant.luogoPlaceholder, 3: t.assistant.aziendaPlaceholder, 5: t.assistant.contattoPlaceholder, 6: t.assistant.messagePlaceholder }[step]}
                    data-testid={`whatsapp-input-step-${step}`}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neon-cyan/60"
                  />
                  <button
                    type="button"
                    onClick={() => (recording ? stopRecording() : startRecording(setters[step]))}
                    data-testid={`whatsapp-mic-step-${step}`}
                    aria-label="Rispondi a voce"
                    className={`rounded-xl border px-3 transition-colors ${recording ? "bg-neon-magenta/20 border-neon-magenta/60 text-neon-magenta animate-pulse" : "bg-white/5 border-white/10 text-white/60 hover:text-neon-cyan hover:border-neon-cyan/40"}`}
                  >
                    {recording ? <Square size={16} /> : <Mic size={16} />}
                  </button>
                  <button type="submit" data-testid={`whatsapp-submit-step-${step}`} className="rounded-xl bg-neon-cyan/20 border border-neon-cyan/40 px-3 text-neon-cyan">
                    <Send size={16} />
                  </button>
                </form>
              )}

              {step === 4 && (
                <div className="flex flex-wrap gap-2">
                  {t.assistant.settoreOptions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setSettore(s.id); setStep(5); }}
                      data-testid={`whatsapp-settore-${s.id}`}
                      className="rounded-full border border-neon-violet/40 px-3 py-2 text-xs text-neon-violet hover:bg-neon-violet/10 transition-colors"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {step === 7 && (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={openWhatsApp}
                    data-testid="whatsapp-open-button"
                    className="flex items-center justify-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-5 py-3 text-sm font-semibold text-void hover:opacity-90 transition-opacity"
                  >
                    <MessageCircle size={16} /> {t.assistant.open}
                  </button>
                  <button onClick={reset} data-testid="whatsapp-restart-button" className="text-xs text-white/50 hover:text-white/80">
                    {t.assistant.restart}
                  </button>
                </div>
              )}

              {step === 0 && (
                <a
                  href={`https://wa.me/${number}?text=${encodeURIComponent(t.common.waDefault)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-direct-link"
                  className="text-xs text-white/50 hover:text-neon-cyan transition-colors mt-auto"
                >
                  {t.assistant.direct}
                </a>
              )}

              {step === 0 && (
                <>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-white/30">
                    <span className="h-px flex-1 bg-white/10" />
                    {t.assistant.or}
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <form
                    onSubmit={(e) => { e.preventDefault(); sendChatText(chatInput); }}
                    className="flex gap-2"
                  >
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={t.assistant.freePlaceholder}
                      data-testid="free-question-input"
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neon-cyan/60"
                    />
                    <button type="submit" data-testid="free-question-submit" className="rounded-xl bg-neon-violet/20 border border-neon-violet/40 px-3 text-neon-violet" aria-label="Invia domanda">
                      <Send size={16} />
                    </button>
                  </form>
                </>
              )}
              </>
              )}

              {chatMode && (
                <>
                  <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1" data-testid="chat-messages">
                    {chatMessages.map((m, i) => (
                      <div
                        key={i}
                        data-testid={`chat-message-${i}`}
                        className={m.role === "user"
                          ? "self-end bg-neon-cyan/15 border border-neon-cyan/30 rounded-2xl rounded-br-sm px-3 py-2 text-sm max-w-[85%]"
                          : "self-start bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-white/80 max-w-[85%]"}
                      >
                        {m.text}
                      </div>
                    ))}
                    {chatLoading && (
                      <p className="text-xs text-neon-cyan animate-pulse" data-testid="chat-thinking">{t.assistant.thinking}</p>
                    )}
                  </div>
                  <a
                    href={`https://wa.me/${number}?text=${encodeURIComponent(t.common.waDefault)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="chat-continue-whatsapp"
                    className="text-xs text-neon-cyan hover:text-white transition-colors"
                  >
                    {t.assistant.continueWhatsApp}
                  </a>
                  <form
                    onSubmit={(e) => { e.preventDefault(); sendChatText(chatInput); }}
                    className="flex gap-2 mt-auto"
                  >
                    <input
                      autoFocus
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={t.assistant.freePlaceholder}
                      data-testid="chat-input"
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neon-cyan/60"
                    />
                    <button type="submit" data-testid="chat-send-button" className="rounded-xl bg-neon-violet/20 border border-neon-violet/40 px-3 text-neon-violet" aria-label="Invia domanda">
                      <Send size={16} />
                    </button>
                  </form>
                  <button onClick={() => setChatMode(false)} data-testid="chat-back-button" className="text-xs text-white/50 hover:text-white/80">
                    {t.assistant.backToGuided}
                  </button>
                </>
              )}
              <p className="text-[10px] text-white/35 leading-relaxed">
                {t.assistant.privacyNote}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-neon-cyan">Privacy Policy</a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) window.dispatchEvent(new Event("xs:open-whatsapp"));
        }}
        data-testid="whatsapp-widget-button"
        aria-label="Apri assistente WhatsApp"
        className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-cyan to-neon-violet flex items-center justify-center text-void"
        style={{ animation: "pulse-glow 3s ease-in-out infinite" }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </motion.button>
    </div>
  );
};

export default WhatsAppAssistant;
